import { Fragment } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import get from 'lodash.get';
import { supportedAmpLocales } from 'dibs-intl/exports/constants';

import { NOT_SUPPORTED_AMP_PAGES } from '../../constants/sbConstants';
import { ServerVarsContextConsumer } from '../../global/ServerVarsContext/ServerVarsContext';
import { addTrailingSlash } from '../../utils/uriUtils';

import { SharedHeadTitleTag } from '../../shared/SharedHead/SharedHeadTitleTag';
import { SharedHeadMetaTags } from '../../shared/SharedHead/SharedHeadMetaTags';
import { SharedHeadLinkTags } from '../../shared/SharedHead/SharedHeadLinkTags';
import { SharedHeadScriptTags } from '../../shared/SharedHead/SharedHeadScriptTags';

/**
 * hreflangs are added via react-helmet for SB pages because of the need for
 * dynamic hreflangs based on url routing (i.e. we want hreflangs for /furniture,
 * but not for /furniture/?page=2). Otherwise, hreflangs logic (for PDPs and AMP PDPs) is handled
 * via the DBL Server API (in prepareLanguageAnnotations*.js files)
 */

function getHrefLangLinks(languageAnnotations = []) {
    return languageAnnotations
        .filter(({ available }) => available)
        .filter(({ href }) => !href.includes('?'))
        .map(({ href, hrefLang }) => ({
            rel: 'alternate',
            href: addTrailingSlash(href),
            hrefLang,
        }));
}

function SbSharedHeadComponent({ itemSearch, isAmp, intlVariables, localeOrigin, imageFallback }) {
    const { meta: metadata, pageType, languageAnnotations } = itemSearch;
    const { locale } = intlVariables;
    const notSupportedAmpPages = pageType && NOT_SUPPORTED_AMP_PAGES.includes(pageType);
    const additionalLinks = [...getHrefLangLinks(languageAnnotations)];

    if (
        supportedAmpLocales.includes(locale) &&
        !metadata.prev &&
        !notSupportedAmpPages &&
        !isAmp &&
        !metadata.disableIndexing
    ) {
        const { canonical } = metadata;
        additionalLinks.push({
            rel: 'amphtml',
            href: addTrailingSlash(`${localeOrigin}/amp/${canonical}`),
        });
    }

    const items = get(itemSearch, 'edges') || [];

    return (
        <Fragment>
            <SharedHeadTitleTag metadata={metadata} />
            <SharedHeadMetaTags metadata={metadata} items={items} imageFallback={imageFallback} />
            <SharedHeadLinkTags metadata={metadata} additionalLinks={additionalLinks} />
            <SharedHeadScriptTags metadata={metadata} />
        </Fragment>
    );
}

SbSharedHeadComponent.propTypes = {
    itemSearch: PropTypes.object.isRequired,
    intlVariables: PropTypes.shape({
        locale: PropTypes.string,
        localeDomain: PropTypes.string,
    }).isRequired,
    isAmp: PropTypes.bool,
    localeOrigin: PropTypes.string.isRequired,
    imageFallback: PropTypes.string,
};

function SbSharedHeadComponentServerVarsContextComponent(props) {
    return (
        <ServerVarsContextConsumer>
            {({ localeOrigin }) => <SbSharedHeadComponent {...props} localeOrigin={localeOrigin} />}
        </ServerVarsContextConsumer>
    );
}

export const SbSharedHead = createFragmentContainer(
    connect(({ intlVariables }) => ({ intlVariables }))(
        SbSharedHeadComponentServerVarsContextComponent
    ),
    {
        itemSearch: graphql`
            fragment SbSharedHead_itemSearch on ItemSearchQueryConnection {
                meta {
                    canonical
                    prev
                    disableIndexing
                    ...SharedHeadTitleTag_metadata
                    ...SharedHeadMetaTags_metadata
                    ...SharedHeadLinkTags_metadata
                    ...SharedHeadScriptTags_metadata
                }
                languageAnnotations {
                    href
                    hrefLang
                    available
                }
                pageType
                edges {
                    node {
                        item {
                            ...SharedHeadMetaTags_node @relay(mask: false)
                        }
                    }
                }
            }
        `,
    }
);
